import { Alert } from 'antd';
import classnames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getAxios } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { BASE_NAME, getParamFromUrl } from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { isAndroid } from '@/infrastructure/product-info';
import { storageGet, storageSet } from '@/infrastructure/storage-utils';
import { useStores } from '@/presentation/store';
import style from './index.module.scss';

export const getLocalBanner = () => {
  try {
    return JSON.parse(storageGet('banner') ?? '');
  } catch (e) {
    return null;
  }
};

interface BannerData {
  closable?: boolean;
  content?: string;
  id?: string;
}

interface IProps {
  showBanner: boolean,
  setShowBanner: React.Dispatch<React.SetStateAction<boolean>>;
  showComplianceBanner: boolean,
  setShowComplianceBanner: React.Dispatch<React.SetStateAction<boolean>>;
  isShowComplianceModal: boolean,
  setIsShowComplianceModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Banner: FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { showBanner, setShowBanner } = props;
  const { showComplianceBanner, setShowComplianceBanner } = props;
  const { setIsShowComplianceModal } = props;
  const localBanner = getLocalBanner();
  const [bannerData, setBannerData] = useState<BannerData>({});
  const [complianceVersion, setComplianceVersion] = useState<string|null>();
  const [showRightlyBanner, setShowRightlyBanner] = useState<boolean>();
  const { loaded } = useStores().ProductStore;
  const { complianceBanner = {} } = useStores().ProductStore;
  const { upgradeRequired, recommendVersion, versionTips } = complianceBanner;
  const { pathname } = useLocation();

  useEffect(() => {
    async function getBanner() {
      const notice = await getAxios().get(`/${API_PREFIX}/api/notice/board-msg-pro`);
      const bannerData = {
        closeable: notice?.data?.closable ?? false,
        content: notice?.data?.content ?? '',
        id: notice?.data?.id ?? '',
      };
      setBannerData(bannerData);
      const isShowBanner = (localBanner?.id !== notice?.data?.id || localBanner?.isShowBanner !== false)
        && !!notice?.data?.content;
      storageSet('banner', JSON.stringify({ ...bannerData, isShowBanner }));
      if (isShowBanner) {
        setShowBanner(true);
        return;
      }
      setShowBanner(false);
    }
    loaded && getBanner();
  }, [loaded]);

  useEffect(() => {
    if (!upgradeRequired) {
      setShowComplianceBanner(false);
      setIsShowComplianceModal(false);
    } else {
      setShowComplianceBanner(true);
      setIsShowComplianceModal(true);
    }
    if (recommendVersion) {
      setComplianceVersion(recommendVersion);
    }
  }, [complianceBanner]);

  useEffect(() => {
    const validPath = pathname.startsWith('/compliance');
    const validState = storageGet('bannerRightly') !== 'false';
    const validTime = moment().isBefore('2025-07-31 23:59:59');
    const valid = validPath && validState && validTime;
    setShowRightlyBanner(valid);
  }, [pathname]);

  if (showComplianceBanner) {
    return (
      <Alert
        banner
        showIcon={false}
        message={
          isAndroid() ? (
            <div className={style.bannerMessage}>
              {t('我们检测到您当前使用的版本并非最新版本。为保障应用合规性，请尽快升级至{{version}}或者更高版本，并保证按最新监管要求初始化SDK。点击', { version: complianceVersion || '4.4.3.3' })}<a onClick={() => {
                const url = new URL(window.location.origin);
                const productId = getParamFromUrl('productId');
                url.pathname = `${BASE_NAME}/compliance-buttetin`;
                if (productId) {
                  url.searchParams.set('productId', productId);
                }
                try {
                  url.searchParams.set('versionTips', encodeURIComponent(JSON.stringify(versionTips)));
                } catch (e) {
                  logger.error(e);
                }
                window.open(url.toString(), '_blank');
              }}>{t('查看详情')}</a>{t('，了解更多信息。')}
            </div>
          ) : (
            <div className={style.bannerMessage}>
              {t('我们检测到您当前使用的版本并非最新版本。为保障应用合规性，请尽快升级至{{version}}或者更高版本，并保证按最新监管要求初始化SDK。点击', { version: complianceVersion || '2.7.51' })}<a onClick={() => {
                const url = new URL(window.location.origin);
                const productId = getParamFromUrl('productId');
                url.pathname = `${BASE_NAME}/compliance-buttetin`;
                if (productId) {
                  url.searchParams.set('productId', productId);
                }
                try {
                  url.searchParams.set('versionTips', encodeURIComponent(JSON.stringify(versionTips)));
                } catch (e) {
                  logger.error(e);
                }
                window.open(url.toString(), '_blank');
              }}>{t('查看详情')}</a>{t('，了解更多信息。')}
            </div>
          )
        }
        className={classnames(style.banner, showComplianceBanner ? '' : style.disableBanner)}
      />
    );
  }

  if (showBanner) {
    return <Alert
      banner
      closable={bannerData.closable}
      showIcon={false}
      message={<div className={style.bannerMessage}>{bannerData.content}</div>}
      className={classnames(style.banner, showBanner ? '' : style.disableBanner)}
      onClose={() => {
        setShowBanner(false);
        storageSet('banner', JSON.stringify({ ...getLocalBanner(), isShowBanner: false }));
      }}
    />;
  }

  // Rightly临时黄条，下线后可删除该代码
  if (showRightlyBanner) {
    return <Alert
      banner
      closable={true}
      showIcon={false}
      message={<div className={style.bannerMessage}>
        {t('Rightly专业版现已正式上线！Rightly将在7月31日从当前平台下架，请前往新平台进行体验：')}
        <a href={'https://rightly.tds.qq.com'} target={'_blank'}>
          https://rightly.tds.qq.com
        </a>
      </div>}
      className={classnames(style.banner)}
      onClose={() => {
        setShowRightlyBanner(false);
        storageSet('bannerRightly', false);
      }}
    />;
  }

  return null;
};
